import { useQuery } from "@tanstack/react-query";
import {
  AssessmentService,
  type AvailableWorkflow,
} from "../services/assessmentService";
import { CalculatorService, type CalculatorLevel } from "../services/calculatorService";
import { ChecklistService } from "../services/checklistService";
import { LabService } from "../services/labService";

/**
 * Utility type to orchestrate the available actions and advice towards the user.
 */
interface UseLabStages {
  baselineProgressionEnabled: boolean;
  baselineAssessment: {
    stage: "not_started" | "in_progress" | "completed";
    adviceMessage: string;
    actions: "preview" | "start" | "view" | "disabled";
  };
  checklist: {
    stage: "not_started" | "in_progress" | "completed";
    adviceMessage: string;
    actions: "preview" | "start" | "view" | "disabled";
  };
  savingsCalculator: {
    stage: "not_started" | "in_progress" | "completed";
    adviceMessage: string;
    actions: "preview" | "start" | "view" | "disabled";
  };
}

/**
 * Compute the stage states based on completion status.
 *
 * Named rather than positional: three of these are adjacent booleans that
 * TypeScript cannot tell apart if transposed.
 */
const computeStages = ({
  baselineComplete,
  checklistSubmitted,
  savingsComplete,
  certificationState,
  baselineProgressionEnabled,
  tier,
  workflow,
}: {
  baselineComplete: boolean;
  checklistSubmitted: boolean;
  savingsComplete: boolean;
  certificationState: string | null | undefined;
  baselineProgressionEnabled: boolean;
  tier: CalculatorLevel;
  /** Null when the gates could not be fetched, which locks everything. */
  workflow: AvailableWorkflow | null;
}): UseLabStages => {
  // Silver's savings step is locked outright rather than previewable: the preview
  // action renders a button that mounts the real calculator, and previewMode only
  // makes inputs read-only, so previewing exposes the whole category list. Bronze
  // keeps its preview — this restriction is Silver-only.
  const savingsLocked = tier === "silver";
  const tierLabel = tier.charAt(0).toUpperCase() + tier.slice(1);
  // Baseline Assessment
  const baselineStage: UseLabStages["baselineAssessment"] = baselineComplete
    ? {
        stage: "completed",
        adviceMessage: baselineProgressionEnabled
          ? "✅ Baseline Assessment completed! Proceed to the Checklist."
          : "✅ Baseline Assessment completed! You can view the Checklist now.",
        actions: "view",
      }
    : {
        stage: "in_progress",
        adviceMessage:
          "Begin the Baseline Assessment to evaluate your current status.",
        actions: "start",
      };

  // Lock state and wording come from the server, which rejects answers sent
  // before every baseline locks.
  const checklistStage: UseLabStages["checklist"] =
    !baselineProgressionEnabled
      ? {
          stage: "not_started",
          adviceMessage: "The Silver checklist is available to view.",
          actions: "preview",
        }
      : checklistSubmitted
        ? {
            stage: "completed",
            adviceMessage:
              "✅ Checklist completed! Proceed to the Savings Calculator.",
            actions: "view",
          }
        : workflow?.checklist_unlocked
          ? {
              stage: "not_started",
              adviceMessage: "Begin the Checklist to evaluate your current status",
              actions: "start",
            }
          : {
              stage: "not_started",
              adviceMessage:
                workflow?.checklist_locked_reason ??
                "Complete the Baseline Assessment to unlock the Checklist.",
              actions: "preview",
            };

  // Savings Calculator
  const savingsStage: UseLabStages["savingsCalculator"] =
    certificationState === "certified"
      ? {
          stage: "completed",
          adviceMessage: "This certification is certified.",
          actions: "view",
        }
      : certificationState === "under_review"
      ? {
          stage: "completed",
          adviceMessage:
            "Your submission is under review by an auditor.",
          actions: "view",
        }
      : !baselineProgressionEnabled || !workflow?.savings_unlocked
      ? {
          stage: "not_started",
          adviceMessage:
            workflow?.savings_locked_reason ??
            "Complete the Checklist to unlock the Savings Calculator.",
          // Silver stays hard-disabled; Bronze keeps its read-only preview.
          actions: savingsLocked ? "disabled" : "preview",
        }
      : savingsComplete
      ? {
          stage: "completed",
          adviceMessage: `✅ ${tierLabel} certification complete! Your submission is waiting for audit.`,
          actions: "view",
        }
      : {
          stage: "not_started",
          adviceMessage:
            "Begin the Savings Calculator to evaluate your current status",
          actions: "start",
        };

  return {
    baselineProgressionEnabled,
    baselineAssessment: baselineStage,
    checklist: checklistStage,
    savingsCalculator: savingsStage,
  };
};

export const useLabStages = (labId: string, tier: CalculatorLevel = "bronze") => {
  return useQuery({
    queryKey: ["lab:stages", labId, tier],
    queryFn: async (): Promise<UseLabStages> => {
      try {
        const labDetails = await LabService.getLabDetails(labId);
        const tierCertification = labDetails.lab.certifications?.find(c => c.level === tier);
        const certificationId = tierCertification?.id;
        const certificationState = tierCertification?.state ?? null;

        // 1. Check Baseline completion - all categories returned for this lab type/tier must be COMPLETED or NOT_APPLICABLE
        const categoriesResp = await CalculatorService.getCategories(
          labId,
          tier,
        );

        const allBaselineComplete =
          categoriesResp.length > 0 &&
          categoriesResp.every(
            (cat) =>
              cat.status === "COMPLETED" || cat.status === "NOT_APPLICABLE",
          );

        // 2. Check Checklist submission - look for tier level submission
        let checklistSubmitted = false;
        try {
          if (certificationId) {
            const checklistResp = await ChecklistService.getCurrentChecklist(
              certificationId,
              "lab",
            );
            checklistSubmitted = checklistResp.instance.status === "submitted";
          }
        } catch (err) {
          console.warn("Failed to fetch checklist status:", err);
          checklistSubmitted = false;
        }

        // 3. Savings completion, baseline progression, and the server's gates.
        let savingsComplete = false;
        let baselineProgressionEnabled = tier !== "silver";
        // Fail closed, so a failed fetch is not a way past the sequence.
        let workflow: AvailableWorkflow | null = null;
        try {
          const available = await AssessmentService.getAvailable(labId, tier);
          baselineProgressionEnabled =
            available.baseline_progression_enabled ?? tier !== "silver";
          workflow = available.workflow;
          savingsComplete =
            available.items.length > 0 &&
            available.items.every(
              (item) =>
                item.savings_submission_state === "LOCKED" ||
                item.savings_submission_state === "NOT_APPLICABLE",
            );
        } catch {
          // If endpoint fails, assume not complete
          savingsComplete = false;
        }

        return computeStages({
          baselineComplete: allBaselineComplete,
          checklistSubmitted,
          savingsComplete,
          certificationState,
          baselineProgressionEnabled,
          tier,
          workflow,
        });
      } catch (error) {
        console.error("Error fetching lab stages:", error);
        // On error, return safe default state
        return {
          baselineProgressionEnabled: tier !== "silver",
          baselineAssessment: {
            stage: "not_started",
            adviceMessage:
              "Begin the Baseline Assessment to evaluate your current status.",
            actions: "start",
          },
          checklist: {
            stage: "not_started",
            adviceMessage:
              "Complete the Baseline Assessment to unlock the Checklist.",
            actions: "preview",
          },
          savingsCalculator: {
            stage: "not_started",
            adviceMessage: "",
            // Fail closed for Silver: "preview" renders a button that mounts the
            // real savings calculator, so a failed stages fetch must not become a
            // way around the lock.
            actions: tier === "silver" ? "disabled" : "preview",
          },
        };
      }
    },
    // Refetch when window regains focus to stay in sync
    refetchOnWindowFocus: true,
    // Keep previous data while refetching to avoid UI flicker
    placeholderData: (previousData) => previousData,
  });
};
